import '../../../../pages/Admin/Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useMemo, useState } from 'react';

import { DefaultRoutes } from 'Routes';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import LessonPreviewModal from '../../Modals/LessonPreviewModal/LessonPreviewModal';
import { Link } from 'react-router-dom';
import { ReactComponent as MagnifierIcon } from 'images/icons/Magnifier.svg';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import { ReactComponent as PlusIcon } from 'images/icons/PlusInCircle.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { getLanguageText } from 'utilities/languages';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  languages: string;
  description: string;
};

export const AddedLessonsTable = observer(() => {
  const ls = useStores().lessonEditorStore;
  const ces = useStores().courseEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ls.lessonsList
        .filter((lesson) => lesson.id && !ces.course.lessonIds.includes(lesson.id))
        .map((lesson) => {
          return {
            id: lesson.id,
            title: lesson.titles.find((title) => title.countryCode === 'en-US')?.text,
            languages: lesson.languages ? lesson.languages.map((lng) => getLanguageText(lng)).join(', ') : '',
            description: lesson.descriptions.find((description) => description.countryCode === 'en-US')?.text,
          };
        }) as RowsDisplay[],
    [JSON.stringify(ls.lessonsList), JSON.stringify(ces.course.lessonIds), ces.course.lessonIds, ls.lessonsList]
  );

  const lessonRawPreview = useCallback(
    () =>
      JSON.stringify(
        ls.lessonsList.find((lesson) => lesson.id === ls.selectedLessonId),
        null,
        2
      ),
    [ls.lessonsList, ls.selectedLessonId]
  );

  return (
    <div className='tmu-table-wrapper admin-edit-section'>
      <div className='table-subheading'>
        <strong>Lessons</strong>
      </div>
      <Table sx={{ minWidth: 750 }} className='tmu-table admin-table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 300 }}>Name</TableCell>
            <TableCell sx={{ width: 900 }}>Languages</TableCell>
            <TableCell sx={{ width: 200 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsDisplay.length ? (
            rowsDisplay.map((lesson) => {
              return (
                <TableRow hover key={lesson.id}>
                  <TableCell>
                    <div className='description-tooltip'>
                      <button className='row-button' onClick={() => ces.course.lessonIds?.push(lesson.id)}>
                        <PlusIcon />
                      </button>
                      {lesson.title.toLowerCase()}
                      <Tooltip title={lesson.description} placement='bottom' className='table-cell-tooltip'>
                        <InfoIcon />
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell>{lesson.languages}</TableCell>
                  <TableCell>
                    <div className='action-buttons'>
                      <button
                        className='row-button'
                        onClick={() => {
                          ls.selectedLessonId = lesson.id;
                          setIsModalOn(true);
                        }}
                      >
                        <MagnifierIcon />
                      </button>
                      <Link to={`/Admin/${DefaultRoutes.LessonEditor}/${lesson.id}`} className='row-button'>
                        <PencilIcon />
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell>
                <Paragraph>
                  <>No lessons left to add.</>
                </Paragraph>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isModalOn ? <LessonPreviewModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} jsonPreview={lessonRawPreview()} /> : <></>}
    </div>
  );
});

export default AddedLessonsTable;
